import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getFontAwesomeIcon, hasValue, isFontawesomeIcon } from '@utils';
import { WidgetDrawerType, WidgetDrawerStatus } from './WidgetDrawer';
/**
 * Sidebar filter panel for widget drawer
 * Shows Type, Status, and Category filters
 */
export const WidgetDrawerFilters = ({ selectedType, selectedStatus, selectedCategory, categories, allowedStatuses, counts, showTypeFilter, showStatusFilter, showCategoryFilter, onTypeChange, onStatusChange, onCategoryChange }) => {
    const context = useContext(UXPContext);
    /**
     * Render Type Filter section (Widgets/UIs)
     */
    const renderTypeFilter = () => {
        if (!showTypeFilter)
            return null;
        return (_jsxs("div", { className: "uxpcore_widgetdrawer__filters__section", children: [_jsx("div", { className: "uxpcore_widgetdrawer__filters__section-title", children: context.$L('uxp-core.wm.type') }), _jsxs("div", { className: classNames('uxpcore_widgetdrawer__filters__option', {
                        'uxpcore_widgetdrawer__filters__option--selected': selectedType === WidgetDrawerType.Widgets
                    }), onClick: () => onTypeChange(WidgetDrawerType.Widgets), children: [_jsxs("div", { className: "uxpcore_widgetdrawer__filters__option-label", children: [_jsx(FontAwesomeIconWrapper, { icon: ['fas', 'th'] }), _jsx("span", { children: context.$L('uxp-core.text.widgets') })] }), _jsx("div", { className: "uxpcore_widgetdrawer__filters__option-count", children: counts.widgets })] }), _jsxs("div", { className: classNames('uxpcore_widgetdrawer__filters__option', {
                        'uxpcore_widgetdrawer__filters__option--selected': selectedType === WidgetDrawerType.UIs
                    }), onClick: () => onTypeChange(WidgetDrawerType.UIs), children: [_jsxs("div", { className: "uxpcore_widgetdrawer__filters__option-label", children: [_jsx(FontAwesomeIconWrapper, { icon: ['fas', 'layer-group'] }), _jsx("span", { children: context.$L('uxp-core.text.custom-uis') })] }), _jsx("div", { className: "uxpcore_widgetdrawer__filters__option-count", children: counts.uis })] })] }));
    };
    /**
     * Render Status Filter section (for widgets only)
     */
    const renderStatusFilter = () => {
        if (!showStatusFilter || selectedType !== WidgetDrawerType.Widgets)
            return null;
        const allStatusOptions = [
            {
                value: WidgetDrawerStatus.All,
                label: context.$L('uxp-core.wm.status-all'),
                icon: ['fas', 'layer-group'],
                count: counts.statuses.all
            },
            {
                value: WidgetDrawerStatus.Installed,
                label: context.$L('uxp-core.text.installed'),
                icon: ['fas', 'check-circle'],
                count: counts.statuses.installed
            },
            {
                value: WidgetDrawerStatus.Added,
                label: context.$L('uxp-core.text.added'),
                icon: ['fas', 'plus-circle'],
                count: counts.statuses.added
            },
            {
                value: WidgetDrawerStatus.NotInstalled,
                label: context.$L('uxp-core.text.not-installed'),
                icon: ['fas', 'exclamation-circle'],
                count: counts.statuses.notInstalled
            }
        ];
        const statusOptions = allStatusOptions.filter(option => allowedStatuses.includes(option.value));
        return (_jsxs("div", { className: "uxpcore_widgetdrawer__filters__section", children: [_jsx("div", { className: "uxpcore_widgetdrawer__filters__section-title", children: context.$L('uxp-core.wm.status') }), statusOptions.map((option) => (_jsxs("div", { className: classNames('uxpcore_widgetdrawer__filters__option', {
                        'uxpcore_widgetdrawer__filters__option--selected': selectedStatus === option.value
                    }), onClick: () => onStatusChange(option.value), children: [_jsxs("div", { className: "uxpcore_widgetdrawer__filters__option-label", children: [_jsx(FontAwesomeIconWrapper, { icon: option.icon }), _jsx("span", { children: option.label })] }), _jsx("div", { className: "uxpcore_widgetdrawer__filters__option-count", children: option.count })] }, option.value)))] }));
    };
    /**
     * Render Category Filter section (for widgets only)
     */
    const renderCategoryFilter = () => {
        if (!showCategoryFilter || selectedType !== 'widgets' || categories.length === 0)
            return null;
        return (_jsxs("div", { className: "uxpcore_widgetdrawer__filters__section", children: [_jsx("div", { className: "uxpcore_widgetdrawer__filters__section-title", children: context.$L('uxp-core.wm.categories') }), categories.map((cat) => {
                    // Render icon
                    let icon = null;
                    if (isFontawesomeIcon(cat.icon)) {
                        const parts = getFontAwesomeIcon(cat.icon);
                        icon = _jsx(FontAwesomeIconWrapper, { icon: parts });
                    }
                    else if (hasValue(cat.icon)) {
                        icon = (_jsx("div", { className: "uxpcore_widgetdrawer__filters__option-icon-bg", style: { backgroundImage: `url(${cat.icon})` } }));
                    }
                    else {
                        icon = _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'folder'] });
                    }
                    return (_jsxs("div", { className: classNames('uxpcore_widgetdrawer__filters__option', {
                            'uxpcore_widgetdrawer__filters__option--selected': selectedCategory === cat.id
                        }), onClick: () => onCategoryChange(cat.id), children: [_jsxs("div", { className: "uxpcore_widgetdrawer__filters__option-label", children: [icon, _jsx("span", { children: context.$L(cat.code) })] }), _jsx("div", { className: "uxpcore_widgetdrawer__filters__option-count", children: cat.count || 0 })] }, cat.id));
                })] }));
    };
    return (_jsxs("div", { className: "uxpcore_widgetdrawer__sidebar", children: [renderTypeFilter(), renderStatusFilter(), renderCategoryFilter()] }));
};
export default WidgetDrawerFilters;
